<script setup lang="ts">
  import { ref, watch, computed, nextTick } from 'vue'
  import { ITemplateStringModel } from '../models/IStringTemplateModel'
  import { Select, Button, Input } from 'ant-design-vue'
  import { ArrowRight, FileJson2Icon } from 'lucide-vue-next'
  import { AvailableColumn } from '@/components/DataCleaning/models/AvailableColumn'
  import { useWindowSize } from '@/composables/useWindowSize'

  interface ITemplateStringBuilderProps {
    configurationLevel: number
    availableColumns: Array<AvailableColumn>
    delimiter_start: string
    delimiter_end: string
    modelValue: ITemplateStringModel
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    placeHolder?: string
  }

  const { controlSize } = useWindowSize()

  const props = defineProps<ITemplateStringBuilderProps>()

  const emit = defineEmits(['update:modelValue'])

  const createDefaultModel = (): ITemplateStringModel =>
    ({
      delimiter: {
        start: props.delimiter_start,
        end: props.delimiter_end
      },
      value: ''
    }) as ITemplateStringModel

  // Initialize an internal model value from the provided prop or from the derived default
  const internalModel = ref<ITemplateStringModel>(
    Object.keys(props.modelValue).length ? props.modelValue : createDefaultModel()
  )

  // If you want a string for the textarea, derive it from internalModel.template.value
  const templateText = ref(internalModel.value.value ?? '')

  const selectedColumn = ref<string | null>(null)

  // Derived column options for <a-select>
  const columnOptions = computed(() =>
    props.availableColumns.map((c: AvailableColumn) => ({
      value: c.value,
      label: c.label
    }))
  )

  // Insert a selected column wrapped with delimiters
  const insertColumn = () => {
    if (!selectedColumn.value) return
    const start = internalModel.value.delimiter?.start ?? props.delimiter_start
    const end = internalModel.value.delimiter?.end ?? props.delimiter_end
    const placeholder = `${start}${selectedColumn.value}${end}`
    // Append to current template text
    templateText.value +=
      (templateText.value && !templateText.value.endsWith(' ') ? ' ' : '') + placeholder
    selectedColumn.value = null
  }

  // Keep internalModel.template.value in sync with templateText
  watch(
    () => templateText.value,
    (val) => {
      internalModel.value = internalModel.value ?? {
        delimiter: { start: props.delimiter_start, end: props.delimiter_end },
        value: ''
      }
      internalModel.value.value = val
      emit('update:modelValue', internalModel.value)
    }
  )

  // If parent updates the v-model from outside, reflect that change locally
  watch(
    () => props.modelValue,
    (newModel) => {
      // If parent provided an empty object (no keys), create a default model and emit it back
      if (!newModel || Object.keys(newModel).length === 0) {
        const defaultModel = createDefaultModel()
        internalModel.value = defaultModel
        templateText.value = defaultModel?.value ?? ''
        // Emit after tick so parent has a chance to have a listener registered
        nextTick(() => emit('update:modelValue', internalModel.value))
        return
      }

      // Normal case: parent passed a model with data
      internalModel.value = newModel
      templateText.value = newModel?.value ?? ''
    },
    { immediate: true }
  )

  const iconClass = computed(() => {
    if (controlSize.value === 'small') return 'text-sm'
    if (controlSize.value === 'large') return 'text-lg'
    return 'text-base'
  })
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex gap-3">
      <Select
        v-model:value="selectedColumn"
        placeholder="Select column"
        :size="controlSize"
        class="w-full"
        style="max-width: 200px"
        :options="columnOptions"
        allow-clear
      />
      <Button
        :disabled="!availableColumns?.length || selectedColumn == null"
        type="primary"
        shape="circle"
        size="large"
        class="flex items-center justify-center"
        @click="insertColumn"
        title="Insert Column"
      >
        <ArrowRight class="text-base" style="line-height: 1; margin: 0" />
      </Button>

      <Input
        v-model:value="templateText"
        :placeholder="props.placeHolder ?? `Enter template`"
        :size="controlSize"
        class="w-full"
        allow-clear
      >
        <template #prefix>
          <!-- decorative watermark icon; non-interactive and slightly faded -->
          <FileJson2Icon :class="[iconClass, 'opacity-30']" style="pointer-events: none; color: currentColor" />
        </template>
      </Input>
    </div>
  </div>
</template>
